// The kitchen display's live push (spec/09 §6.3; F-FNB-04). While the organisation's realtime socket
// is live, this subscribes to `venue:{v}:kitchen` and applies each `kitchen.ticket.*` frame — which
// carries the whole ticket — to the board it already shows, without a refetch. The frames carry no
// per-channel `version` (they ride the outbox dispatcher's sink, not the floor coalescer), so applying
// the whole ticket is idempotent and the REST snapshot stays the recovery mechanism: on every (re)connect
// both boards are refetched, so a frame missed while disconnected converges rather than drifting (spec/09
// §4). When the socket is not live (reduced motion, or disconnected) the boards poll (`queries.ts`).

import type { KitchenTicket } from '@clavian/api-client';
import { channels, KITCHEN_TICKET_CREATED, KITCHEN_TICKET_UPDATED } from '@clavian/contracts/constants';
import type { TypeId } from '@clavian/contracts/typeid';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useChannelEvents, useRealtimeLive } from '../../realtime';
import { kitchenKeys } from './queries';

type Board = readonly KitchenTicket[];

function upsert(board: Board, ticket: KitchenTicket): Board {
  const index = board.findIndex((held) => held.id === ticket.id);
  if (index === -1) return [...board, ticket];
  const next = board.slice();
  next[index] = ticket;
  return next;
}

function without(board: Board, id: string): Board {
  return board.filter((held) => held.id !== id);
}

/** Route the ticket to the board its state belongs on, removing it from the other (spec/13 §6.2). */
function applyTicket(queryClient: QueryClient, organizationId: string, venueId: string, ticket: KitchenTicket): void {
  const activeKey = kitchenKeys.tickets(organizationId, venueId, 'active');
  const deliveredKey = kitchenKeys.tickets(organizationId, venueId, 'delivered');
  const onActive = ticket.state === 'new' || ticket.state === 'accepted' || ticket.state === 'preparing' || ticket.state === 'ready';
  const onDelivered = ticket.state === 'delivered';
  queryClient.setQueryData<Board>(activeKey, (old) => (old === undefined ? old : onActive ? upsert(old, ticket) : without(old, ticket.id)));
  queryClient.setQueryData<Board>(deliveredKey, (old) => (old === undefined ? old : onDelivered ? upsert(old, ticket) : without(old, ticket.id)));
}

/**
 * Keep the kitchen boards live from the channel while the socket is live. Returns whether the push
 * is active, so the display can drop its interval.
 */
export function useKitchenLive(organizationId: string, venueId: string): boolean {
  const queryClient = useQueryClient();
  const live = useRealtimeLive();

  useEffect(() => {
    if (!live) return;
    // Snapshot-is-recovery (spec/09 §4): resync both boards whenever the socket comes (back) up.
    void queryClient.invalidateQueries({ queryKey: kitchenKeys.tickets(organizationId, venueId, 'active') });
    void queryClient.invalidateQueries({ queryKey: kitchenKeys.tickets(organizationId, venueId, 'delivered') });
  }, [live, organizationId, venueId, queryClient]);

  useChannelEvents(
    channels.venue(venueId as TypeId<'ven'>, 'kitchen'),
    (envelope) => {
      if (envelope.type !== KITCHEN_TICKET_CREATED && envelope.type !== KITCHEN_TICKET_UPDATED) return;
      const ticket = (envelope.p as { ticket?: KitchenTicket } | undefined)?.ticket;
      if (ticket === undefined) return;
      applyTicket(queryClient, organizationId, venueId, ticket);
    },
    live,
  );

  return live;
}
